import React, {Component} from 'react'

import {
    Grid,
    GridCell,
    DataCell,
    DataTable,
    DataBody,
    DataHeader,
    DataRow,
    Spinner,
    css,
    TextInput,
    Select,
    Button,
    Icon,
    Theme,
} from "@bdfx/mergado-ui-kit"

import {superadminGetMergadoQueriesInfo} from "app/helpers/api";
import { AxiosResponse } from 'axios';
import trans from 'trans';

export const ops = ['=', '!=', '~', '<=', '>=', '<','>', 'in']

export type MergadoFilterType = {
    field: string
    op: '=' | '!=' | '~' | '<=' | '>=' | '<' | '>' | 'in'
    value: string | number | boolean | null
}

const apiFields = [
    'name',
    'id',
    'project_id',
    'created_at',
    'search_output',
    'advanced_interface',
    'read_only',
    'product_count',
    'form_data',
    'hash',
    'query',
]

interface Props {
    selectedProjectId: number
}

interface State {
    queriesInfo: QueriesInfo[]
    isLoading: boolean
    fields: string[]
    filter: MergadoFilterType[]
    editFilterIndex: number
}

export type QueriesInfo = {
    id: string
    project_id: string
    name: string
    created_at: string
    search_output: boolean
    advanced_interface: boolean
    read_only: boolean
    product_count: number
    form_data: string
    hash: string
    query: string
}

class MergadoQueries extends Component<Props, State> {
    constructor(props) {
        super(props)

        this.state = {
            isLoading: false,
            queriesInfo: null,
            fields: [
                'id',
                'name',
                'search_output',
                'product_count',
            ],
            filter: [{ field: null, op: null, value: null }],
            editFilterIndex: 0,
        }
    }

    getMergadoQueries = (fields: string[], filter: MergadoFilterType[]) => {
        const {selectedProjectId} = this.props

        this.setState({isLoading: true})
        superadminGetMergadoQueriesInfo(selectedProjectId, fields, filter)
            .then((response: AxiosResponse<QueriesInfo[]>) => {
                this.setState({queriesInfo: response.data})
            })
            .catch(error => {
                console.error(error.response)
            })
            .finally(() => {
                this.setState({isLoading: false})
            })
    }

    componentDidMount() {
        if (this.props.selectedProjectId) {
            this.getMergadoQueries(this.state.fields, this.state.filter)
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
        if (prevProps && prevProps.selectedProjectId !== this.props.selectedProjectId && this.props.selectedProjectId) {
            this.getMergadoQueries(this.state.fields, this.state.filter)
        }
    }

    render() {
        const { isLoading, queriesInfo, fields, filter, editFilterIndex } = this.state

        if (isLoading) {
            return <div>Loading Mergado queries <Spinner type={'bubbles'} /></div>
        }

        if (!queriesInfo) {
            return <span />
        }

        const selectFieldsOptions = apiFields.map((o: string) => {
            return {
                label: `${o}`,
                value: `${o}`,
            }
        })

        const selectOpsOptions = ops.map((o: string) => {
            return {
                label: `${o}`,
                value: `${o}`,
            }
        })

        return (
            <div>
                <Grid cols={'repeat(5, 1fr)'}>
                    <GridCell>
                        fields: <Select
                            options={selectFieldsOptions}
                            value={fields}
                            multiple={true}
                            onChange={(value) => this.setState({ fields: value })}
                        />
                    </GridCell>
                    <GridCell>field:
                        <Select
                            options={selectFieldsOptions}
                            value={filter[editFilterIndex].field}
                            onChange={(value) => {
                                let updatedFilter = [...filter]
                                updatedFilter[editFilterIndex].field = value
                                this.setState({ filter: updatedFilter })
                            }}
                            onClear={() => {
                                let updatedFilter = [...filter]
                                updatedFilter[editFilterIndex].field = null
                                this.setState({ filter: updatedFilter })
                            }}
                        />
                    </GridCell>
                    <GridCell>op:
                        <Select
                            options={selectOpsOptions}
                            value={filter[editFilterIndex].op}
                            onChange={(value) => {
                                let updatedFilter = [...filter]
                                updatedFilter[editFilterIndex].op = value
                                this.setState({ filter: updatedFilter })
                            }}
                            onClear={() => {
                                let updatedFilter = [...filter]
                                updatedFilter[editFilterIndex].op = null
                                this.setState({ filter: updatedFilter })
                            }}
                        />
                    </GridCell>
                    <GridCell>value:
                        <Grid cols={'repeat(4, max-content)'}>
                            <TextInput
                                value={typeof filter[editFilterIndex].value === 'string' ? `${filter[editFilterIndex].value}` : ''}
                                onChange={(value) => {
                                    let updatedFilter = [...filter]
                                    updatedFilter[editFilterIndex].value = value
                                    this.setState({ filter: updatedFilter })
                                }}
                                onClear={() => {
                                    let updatedFilter = [...filter]
                                    updatedFilter[editFilterIndex].value = null
                                    this.setState({ filter: updatedFilter })
                                }}
                            />
                            <Button
                                type={'void'}
                                color={'nocolor'}
                                onClick={() => {
                                    let updatedFilter = [...filter]
                                    updatedFilter[editFilterIndex].value = true
                                    this.setState({ filter: updatedFilter })
                                }}
                                icon={<Icon type="toggle-on" color={filter[editFilterIndex].value === true ? Theme.green : Theme.grey} text={'true'} />}
                            />
                            <Button
                                type={'void'}
                                color={'nocolor'}
                                onClick={() => {
                                    let updatedFilter = [...filter]
                                    updatedFilter[editFilterIndex].value = false
                                    this.setState({ filter: updatedFilter })
                                }}
                                icon={<Icon type="toggle-off" color={filter[editFilterIndex].value === false ? Theme.green : Theme.grey} text={'false'} />}
                            />
                            <Button
                                type={'void'}
                                color={'nocolor'}
                                onClick={() => {
                                    let updatedFilter = [...filter]
                                    updatedFilter[editFilterIndex].value = null
                                    this.setState({ filter: updatedFilter })
                                }}
                                icon={<Icon type="circle" color={filter[editFilterIndex].value === null ? Theme.green : Theme.grey} text={'null'} />}
                            />
                        </Grid>
                    </GridCell>
                    <GridCell style={{ alignSelf: 'center' }}>
                        <Button
                            type={'void'}
                            onClick={() => this.getMergadoQueries(fields, filter)}
                            icon={<Icon type={'filter'} text={trans('filter')} />}
                        />
                    </GridCell>
                </Grid>

                <DataTable onRowSelected={() => {
                }}>
                    <DataHeader>
                        <DataCell type={"header"}>query ID</DataCell>
                        <DataCell type={"header"}>name</DataCell>
                        <DataCell type={"header"}>products count</DataCell>
                        <DataCell type={"header"}>query</DataCell>
                        <DataCell type={"header"}>search output</DataCell>
                        <DataCell type={"header"}>advanced interface</DataCell>
                        <DataCell type={"header"}>form data</DataCell>
                        <DataCell type={"header"}>hash</DataCell>
                        <DataCell type={"header"}>created at</DataCell>
                    </DataHeader>
                    <DataBody>
                        {queriesInfo.map(query => {
                            return <DataRow dataId={'query' + query.id} key={query.id}>
                                <DataCell>{query.id}</DataCell>
                                <DataCell>{query.name}</DataCell>
                                <DataCell>{query.product_count}</DataCell>
                                <DataCell><StyledLargeDiv>{query.query}</StyledLargeDiv></DataCell>
                                <DataCell>{query.search_output ? 'true' : 'false'}</DataCell>
                                <DataCell>{query.advanced_interface ? 'true' : 'false'}</DataCell>
                                <DataCell><StyledLargeDiv>{query.form_data}</StyledLargeDiv></DataCell>
                                <DataCell>{query.hash}</DataCell>
                                <DataCell>{query.created_at}</DataCell>
                            </DataRow>
                        })}
                    </DataBody>
                </DataTable>
            </div>
        )
    }
}

const StyledLargeDiv = css('div')({
    maxWidth: '220px',
    maxHeight: '90px',
    overflow: 'auto',
})

export default MergadoQueries